import React, { useState, useEffect } from 'react';
import { AlertPopup } from './common/AlertPopup';
import axios from 'axios';

export function SchedulingMetricsModal({ isOpen, onClose, metricType, currentValues, projectData }) {
  const [values, setValues] = useState({
    used: currentValues?.used || '',
    total: currentValues?.total || ''
  });
  const [errors, setErrors] = useState({
    used: '',
    total: ''
  });
  const [alert, setAlert] = useState({ show: false, message: '', isError: false });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset form values when currentValues changes
  useEffect(() => {
    setValues({
      used: currentValues?.used || '',
      total: currentValues?.total || ''
    });
  }, [currentValues]);

  // Reset success state when modal is opened/closed
  useEffect(() => {
    if (!isOpen) {
      setIsSuccess(false);
    }
  }, [isOpen]);

  const getTitle = () => {
    switch (metricType) {
      case 'mobs':
        return 'Update Mobilizations';
      case 'days':
        return 'Update Days';
      case 'trips':
        return 'Update Trips';
      default:
        return 'Update Metrics';
    }
  };

  const getLabels = () => {
    switch (metricType) {
      case 'mobs':
        return { used: 'Mobs Used', total: 'Mobs Contracted' };
      case 'days':
        return { used: 'Days Used', total: 'Days Estimated' };
      case 'trips':
        return { used: 'Trips Used', total: 'Trips Estimated' };
      default:
        return { used: 'Used', total: 'Total' };
    }
  };

  const validateValues = () => {
    // Remove the validation check for used > total
    return true;
  };

  const handleInputChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    // Remove the validation check for used > total
    setErrors(prev => ({ ...prev, used: '' }));
  };

  const handleSubmit = async () => {
    if (!validateValues()) {
      return;
    }

    if (!projectData?.opportunityNo) {
      setAlert({
        show: true,
        message: 'Project data is missing',
        isError: true
      });
      return;
    }

    setIsLoading(true);
    try {
      // Convert opportunityNo to number
      const opportunityId = parseInt(projectData.opportunityNo, 10);
      
      if (isNaN(opportunityId)) {
        throw new Error('Invalid Opportunity Number');
      }

      const payload = {
        Opportunity_System_Wide_ID__c: opportunityId,
        mobs_used: metricType === 'mobs' ? parseInt(values.used) || 0 : 0,
        mobs_contracted: metricType === 'mobs' ? parseInt(values.total) || 0 : 0,
        days_used: metricType === 'days' ? parseInt(values.used) || 0 : 0,
        days_estimated: metricType === 'days' ? parseInt(values.total) || 0 : 0,
        trip_used: metricType === 'trips' ? parseInt(values.used) || 0 : 0,
        trip_estimated: metricType === 'trips' ? parseInt(values.total) || 0 : 0
      };

      try {
        // First check if schedule exists
        const checkResponse = await axios.get(
          `https://projects-dev.nettlescs.com:5000/api/schedules/${opportunityId}`
        );

        // If schedule exists, preserve existing values
        if (checkResponse.data.status === "success" && checkResponse.data.data) {
          const existingData = checkResponse.data.data;
          payload.mobs_used = metricType === 'mobs' ? parseInt(values.used) || 0 : existingData.mobs_used;
          payload.mobs_contracted = metricType === 'mobs' ? parseInt(values.total) || 0 : existingData.mobs_contracted;
          payload.days_used = metricType === 'days' ? parseInt(values.used) || 0 : existingData.days_used;
          payload.days_estimated = metricType === 'days' ? parseInt(values.total) || 0 : existingData.days_estimated;
          payload.trip_used = metricType === 'trips' ? parseInt(values.used) || 0 : existingData.trip_used;
          payload.trip_estimated = metricType === 'trips' ? parseInt(values.total) || 0 : existingData.trip_estimated;

          // PUT request for existing schedule
          await axios.put(
            `https://projects-dev.nettlescs.com:5000/api/schedules/${opportunityId}`,
            payload,
            {
              headers: { 'Content-Type': 'application/json' }
            }
          );
        } else {
          throw new Error('Schedule not found'); // Force catch block to do POST
        }
      } catch (error) {
        // If schedule doesn't exist or GET fails, do POST request
        await axios.post(
          'https://projects-dev.nettlescs.com:5000/api/schedules',
          payload,
          {
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      setIsSuccess(true);
    } catch (error) {
      console.error("Error updating schedule:", error);
      setAlert({
        show: true,
        message: error.message === 'Invalid Opportunity Number' 
          ? 'Invalid Opportunity Number format'
          : 'Failed to update schedule. Please try again.',
        isError: true
      });
    }
    setIsLoading(false);
  };

  const handleCloseAlert = () => {
    setAlert({ show: false, message: '', isError: false });
  };

  const handleClose = (success = false) => {
    if (success && isSuccess) { // Only trigger parent refresh if we're closing after a successful update
      onClose(true);
    } else {
      onClose(false);
    }
    setValues({ used: '', total: '' });
    setIsSuccess(false);
  };

  if (!isOpen) return null;

  const labels = getLabels();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50">
      {alert.show && (
        <AlertPopup
          message={alert.message}
          onClose={handleCloseAlert}
          isError={alert.isError}
        />
      )}
      <div className="relative w-1/2 bg-white rounded-lg mt-[100px]">
        <button
          onClick={() => handleClose()}
          className="absolute -top-10 -right-1 text-white text-3xl font-normal cursor-pointer z-50"
        >
          ×
        </button>

        <div className="sticky top-0 bg-white p-6 border-b border-gray-200 rounded-t-lg z-40 shadow-sm">
          <h2 className="text-xl font-semibold">{getTitle()}</h2>
        </div>

        <div className="p-6">
          {isSuccess ? (
            <div className="min-h-[200px] flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                Schedule Updated Successfully!
              </h2>
              <button
                onClick={() => handleClose(true)}
                className="bg-red-700 text-white py-2 px-8 rounded-lg hover:bg-red-800"
              >
                Done
              </button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{labels.used}</label>
                  <input
                    type="number"
                    min="0"
                    value={values.used}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue === '' || parseInt(newValue) >= 0) {
                        handleInputChange('used', newValue);
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        handleInputChange('used', '0');
                      }
                    }}
                    className={`w-full p-2 border rounded-md ${errors.used ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.used && (
                    <p className="text-red-500 text-sm mt-1">{errors.used}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{labels.total}</label>
                  <input
                    type="number"
                    min="0"
                    value={values.total}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue === '' || parseInt(newValue) >= 0) {
                        handleInputChange('total', newValue);
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        handleInputChange('total', '0');
                      }
                    }}
                    className="w-full p-2 border rounded-md"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  className="w-1/4 bg-red-700 text-white py-2 px-4 rounded-lg hover:bg-red-800 disabled:opacity-50"
                  onClick={handleSubmit}
                  disabled={isLoading || errors.used !== ''}
                >
                  {isLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
} 